import { 
  doc, 
  getDoc, 
  setDoc, 
  Timestamp 
} from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';
import { db } from '../firebase';
import { 
  PlayerStats, 
  PlayerStyleMetrics, 
  PlayerAchievementState,
  PlayerAchievementProgress
} from '../types';
import { ACHIEVEMENTS } from '../constants/achievements';

export const fetchPlayerAchievementState = async (userId: string): Promise<PlayerAchievementState | null> => {
  const snap = await getDoc(doc(db, 'users', userId, 'achievements', 'state'));
  if (snap.exists()) {
    return snap.data() as PlayerAchievementState;
  }
  return null;
};

export const evaluateAchievements = async (
  userId: string, 
  providedData?: { 
    stats?: PlayerStats; 
    style?: PlayerStyleMetrics; 
    profile?: any; 
    achievementState?: PlayerAchievementState 
  }
): Promise<void> => {
  try {
    // 1. Check if we have provided data or need to fetch
    let stats = providedData?.stats || null;
    let style = providedData?.style || null;
    let profile = providedData?.profile || null;
    let currentState = providedData?.achievementState || null;

    if (!stats || !currentState) {
      const [statsSnap, styleSnap, profileSnap, achievementSnap] = await Promise.all([
        stats ? Promise.resolve(null) : getDoc(doc(db, 'users', userId, 'stats', 'global')),
        style ? Promise.resolve(null) : getDoc(doc(db, 'users', userId, 'stats', 'style')),
        profile ? Promise.resolve(null) : getDoc(doc(db, 'users', userId)),
        currentState ? Promise.resolve(null) : getDoc(doc(db, 'users', userId, 'achievements', 'state'))
      ]);

      if (statsSnap && statsSnap.exists()) stats = statsSnap.data() as PlayerStats;
      if (styleSnap && styleSnap.exists()) style = styleSnap.data() as PlayerStyleMetrics;
      if (profileSnap && profileSnap.exists()) profile = profileSnap.data();
      if (achievementSnap && achievementSnap.exists()) currentState = achievementSnap.data() as PlayerAchievementState;
    }

    if (!stats) return;
    
    if (!currentState) {
      currentState = {
        achievements: {},
        unlockedBadgeIds: [],
        unlockedTitleIds: [],
        activeTitleId: null
      };
    }

    const newAchievements = { ...currentState.achievements };
    const newBadgeIds = [...currentState.unlockedBadgeIds];
    const newTitleIds = [...currentState.unlockedTitleIds];
    let changed = false;

    for (const def of ACHIEVEMENTS) {
      const progress = newAchievements[def.id] || {
        achievementId: def.id,
        unlocked: false,
        currentValue: 0,
        targetValue: def.targetValue,
        progressPct: 0
      };

      if (progress.unlocked && !def.repeatable) continue;

      let currentValue = 0;
      if (def.metricKey.startsWith('robotWins_')) {
        const level = def.metricKey.split('_')[1];
        currentValue = stats.robotWins?.[level] || 0;
      } else if (def.metricKey === 'uniqueBoards') {
        currentValue = stats.uniqueBoards?.length || 0;
      } else if (def.metricKey === 'uniqueLanguages') {
        currentValue = stats.uniqueLanguages?.length || 0;
      } else if (def.metricKey === 'profileComplete') {
        currentValue = (profile?.displayName && profile?.photoURL && profile?.bio) ? 1 : 0;
      } else {
        currentValue = (stats as any)[def.metricKey] || 0;
      }

      const progressPct = Math.min(100, (currentValue / def.targetValue) * 100);
      const isUnlocked = currentValue >= def.targetValue;

      if (isUnlocked && !progress.unlocked) {
        progress.unlocked = true;
        progress.unlockedAt = Timestamp.now();
        changed = true;

        if (def.rewardBadgeId && !newBadgeIds.includes(def.rewardBadgeId)) {
          newBadgeIds.push(def.rewardBadgeId);
        }
        if (def.rewardTitleId && !newTitleIds.includes(def.rewardTitleId)) {
          newTitleIds.push(def.rewardTitleId);
        }
      }

      if (currentValue !== progress.currentValue || progressPct !== progress.progressPct) {
        newAchievements[def.id] = {
          ...progress,
          currentValue,
          progressPct
        };
        changed = true;
      }
    }

    if (changed) {
      await setDoc(doc(db, 'users', userId, 'achievements', 'state'), {
        achievements: newAchievements,
        unlockedBadgeIds: newBadgeIds,
        unlockedTitleIds: newTitleIds,
        activeTitleId: currentState.activeTitleId,
        updatedAt: Timestamp.now()
      }, { merge: true });
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, `users/${userId}/achievements/state`);
  }
};
